/**
 * Validation Helper Functions for CreateBlankModal
 * Extracted to reduce cognitive complexity of main component
 */

// Helper: Validate basic required fields
export const validateBasicFields = (formData) => {
  const errors = {};
  if (!formData) return errors;
  if (!formData.contractTitle) {
    errors.contractTitle = "Please enter a contract title";
  }
  if (!formData.businessType) {
    errors.businessType = "Please select a business type from the dropdown";
  }
  if (!formData.contractType) {
    errors.contractType = "Please select a contract type from the dropdown";
  }
  if (!formData.ownerDepartment) {
    errors.ownerDepartment = "Please select an owner department from the dropdown";
  }
  if (!formData.startDate) {
    errors.startDate = "Please select a start date";
  }
  if (!formData.status_id) {
    errors.status_id = "Please select a status from the dropdown";
  }
  if (!formData.region_id) {
    errors.region_id = "Please select a region from the dropdown";
  }
  return errors;
};

// Helper: Check if end date is in the past (less than today)
export const isEndDateInPast = (endDate, isAutoRenewal) => {
  if (!endDate || isAutoRenewal || String(endDate).startsWith('1999-01-01') || String(endDate).startsWith('1900-01-01')) {
    return false;
  }
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const parts = String(endDate).slice(0, 10).split('-').map(Number);
  if (parts.length === 3 && !isNaN(parts[0]) && !isNaN(parts[1]) && !isNaN(parts[2])) {
    const end = new Date(parts[0], parts[1] - 1, parts[2]);
    return end < today;
  }
  const end = new Date(endDate);
  return !isNaN(end.getTime()) && end < today;
};

// Helper: Validate dates logic
export const validateDateFields = (formData, isAutoRenewal, isRenewalDateOptional = false) => {
  const errors = {};
  if (!formData) return errors;
  if (!isAutoRenewal) {
    if (!formData.endDate) {
      errors.endDate = 'Please select an end date';
    }
    if (!isRenewalDateOptional && !formData.renewalDate) {
      errors.renewalDate = 'Please select a renewal date';
    }
  }
  
  if (formData.startDate && formData.endDate && new Date(formData.startDate) >= new Date(formData.endDate)) {
    errors.endDate = "End date must be after start date";
  }

  const isPast = isEndDateInPast(formData.endDate, isAutoRenewal);
  if (!isRenewalDateOptional) {
    if (formData.startDate && formData.renewalDate && new Date(formData.startDate) >= new Date(formData.renewalDate)) {
      errors.renewalDate = "Renewal date must be after start date";
    }
    if (formData.endDate && formData.renewalDate && new Date(formData.endDate) >= new Date(formData.renewalDate)) {
      errors.renewalDate = "Renewal date must be after end date";
    }
  } else if (formData.renewalDate && !isPast) {
    if (formData.startDate && new Date(formData.startDate) >= new Date(formData.renewalDate)) {
      errors.renewalDate = "Renewal date must be after start date";
    }
    if (formData.endDate && new Date(formData.endDate) >= new Date(formData.renewalDate)) {
      errors.renewalDate = "Renewal date must be after end date";
    }
  }
  return errors;
};

// Helper: Validate client/vendor fields
export const validatePartyFields = (formData) => {
  const errors = {};
  if (!formData) return errors;
  if (formData.businessType === 'Client') {
    if (!formData.clientName) errors.clientName = "Please select a client from the dropdown";
  } else if (formData.businessType === 'Vendor') {
    if (!formData.vendorName) errors.vendorName = "Please select a vendor from the dropdown";
    if (!formData.vendorEmail) errors.vendorEmail = "Please select a vendor email from the dropdown";
  }
  return errors;
};

// Helper: Validate description
export const validateDescription = (formData) => {
  const errors = {};
  if (formData && (!formData.description || !formData.description.trim() || formData.description.trim().length <= 10)) {
    errors.description = 'Description must be more than 10 characters';
  }
  return errors;
};

// Helper: Validate contract type specific fields
export const validateContractTypeFields = (formData, contractTypes, contractSelection) => {
  const errors = {};
  if (!formData) return errors;
  const contractTypeLabel = getSelectedContractTypeLabel(formData.contractType, contractTypes);
  
  if (contractTypeLabel.includes("Purchase Order") && !formData.po_number?.trim()) {
    errors.po_number = "PO Number is required";
  }
  
  if ((contractTypeLabel.includes("SOW") || contractTypeLabel.includes("PO")) && !formData.project_id) {
    errors.project_id = "Please select a project from the dropdown";
  }
  
  if (contractSelection === 'existing' && !formData.year) {
    errors.year = "Please select a year from the dropdown";
  }
  
  return errors;
};

// Helper: Validate parent contract
export const validateParentContract = (formData, contractTypes, isAmendment, isAddendum) => {
  const errors = {};
  if (!formData) return errors;
  const contractTypeLabel = getSelectedContractTypeLabel(formData.contractType, contractTypes);
  
  if ((!contractTypeLabel.includes("MSA") && !contractTypeLabel.includes("NDA")) || isAmendment || isAddendum) {
    if (!formData.parent_contract_id) {
      errors.parent_contract_id = "Please select a parent contract from the dropdown";
    }
  }
  
  return errors;
};

// Helper: Validate termination
export const validateTermination = (formData, statuses, isAutoRenewal) => {
  const errors = {};
  if (!formData || !statuses) return errors;
  const isTerminated = formData.status_id && 
    statuses.find(s => s.id === Number(formData.status_id))?.value === "Terminated";

  if (isTerminated) {
    if (!formData.termination_date) {
      errors.termination_date = "Termination Date is required";
    }
    if (!formData.termination_reason?.trim()) {
      errors.termination_reason = "Termination Reason is required";
    }
  }

  return errors;
};

// Helper: Validate file
export const validateFile = (uploadedFile) => {
  const errors = {};
  if (!uploadedFile) return errors;
  if (uploadedFile) {
    const allowedExtensions = ['.doc', '.docx', '.pdf'];
    const fileExt = uploadedFile.name.toLowerCase().slice(uploadedFile.name.lastIndexOf('.'));
    if (!allowedExtensions.includes(fileExt)) {
      errors.file = "Only .doc, .docx, and .pdf files are allowed";
    }
  }
  return errors;
};

// Main validation function - orchestrates all helpers
export const validateForm = (
  formData,
  contractTypes,
  contractSelection,
  statuses,
  isAmendment,
  isAddendum,
  isAutoRenewal,
  uploadedFile
) => {
  const selectedStatus = Array.isArray(statuses) && formData?.status_id
    ? statuses.find(s => String(s.id) === String(formData.status_id) || String(s.value) === String(formData.status_id))?.value
    : null;
  const statusName = (selectedStatus || formData?.contract_status || formData?.status || '').trim().toLowerCase();
  const isCompleted = statusName === 'completed';
  const isPastEnd = isEndDateInPast(formData?.endDate, isAutoRenewal);
  const isRenewalDateOptional = isCompleted || isPastEnd;

  return {
    ...validateBasicFields(formData),
    ...validateDateFields(formData, isAutoRenewal, isRenewalDateOptional),
    ...validatePartyFields(formData),
    ...validateDescription(formData),
    ...validateContractTypeFields(formData, contractTypes, contractSelection),
    ...validateParentContract(formData, contractTypes, isAmendment, isAddendum),
    ...validateTermination(formData, statuses, isAutoRenewal),
    ...validateFile(uploadedFile)
  };
};

// Utility: Get selected contract type label
export const getSelectedContractTypeLabel = (contractTypeId, contractTypes) => {
  if (!Array.isArray(contractTypes)) return "";
  const selected = contractTypes.find(
    ct => String(ct.id) === String(contractTypeId)
  );
  return selected?.value || "";
};
